import React, { createContext, useMemo, useCallback, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import {
  NotificationService,
  NOTIFICATION_TYPE_ERROR,
  NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_DURATIONS,
} from 'ui-components/services/notification-service';

import { BY_VOLUME, BY_WEIGHT_CALC_TYPE, SKIP_DIMENSIONS, MAX_WEIGHT, ADD } from './constants';
import DeliveryOptionsApiService from './delivery-options-api-service';
import useNonLocalDeliveryServicesAvailableForSeller from './edit-delivery-option-page/use-non-local-delivery-services-available-for-seller';
import { additionalFeesEnum, pricingModelEnum } from './enums';
import { useCalculationMethodType } from './hooks';
import RegistrationStatusService from './registrationStatusService';
import GetMarketplaceDisplayName from '../../../../../components/data-driven-logo/get-marketplace-display-name';
import { AU, NZ } from '../../../../../constants/country-codes';
import { USD } from '../../../../../constants/currency-codes';
import { emptyObject } from 'ui-components/default-props/empty';
import { MarketplaceIds } from '../../../../../enums/marketplace-ids';
import { convertDimensions } from '../../../../../services/unit-services/dimensions/dimension-convert-service';
import { convertWeight } from '../../../../../services/unit-services/weights/weight-convert-service';
import Global from '../../../../../store/global-selectors';
import { useSubscribableState } from '../../../../../subscribable-state';
import { getStore, saveStoreSettings } from '../../../api/store-api';
import messages from '../messages';

const readFromBackend = async (storeGuid) => {
  try {
    const response = await DeliveryOptionsApiService.getDeliveryOptions(storeGuid);
    return response.settings;
  } catch {
    NotificationService.showNotification({
      type: NOTIFICATION_TYPE_ERROR,
      title: <FormattedMessage {...messages.errorLoadingDeliveryOptionsTitle} />,
      message: <FormattedMessage {...messages.errorLoadingDeliveryOptionsMessage} />,
      duration: NOTIFICATION_DURATIONS.MEDIUM,
    });
  }
  return [];
};

const writeToBackend = async (storeGuid, newItems, updatedId) => {
  try {
    const response = await DeliveryOptionsApiService.saveDeliveryOptions(storeGuid, updatedId, {
      settings: newItems,
    });
    return response.settings;
  } catch {
    NotificationService.showNotification({
      type: NOTIFICATION_TYPE_ERROR,
      title: <FormattedMessage {...messages.errorSavingDeliveryOptionsTitle} />,
      message: <FormattedMessage {...messages.errorSavingDeliveryOptionsMessage} />,
      duration: NOTIFICATION_DURATIONS.MEDIUM,
    });
  }
  return newItems;
};

export const getStoreStatus = async (storeGuid) => {
  const status = await DeliveryOptionsApiService.getStoreStatus(storeGuid);
  return RegistrationStatusService.convertRegistrationStatusToFrontendFormat(status);
};

const callRegisterStoreEndpoint = async (storeGuid) => {
  const status = await DeliveryOptionsApiService.registerStore(storeGuid);
  return RegistrationStatusService.convertRegistrationStatusToFrontendFormat(status);
};

export const getShopifyIsValid = async (storeGuid) => {
  return DeliveryOptionsApiService.checkShopifyStatus(storeGuid);
};

export const getMagentoIsValid = async (storeGuid) => {
  return DeliveryOptionsApiService.checkSupportsDeliveryOptions(storeGuid);
};

export const getWooCommerceIsValid = async (storeGuid) => {
  return DeliveryOptionsApiService.checkSupportsDeliveryOptions(storeGuid);
};

const addNewItem = (items, newItem) => {
  const realNewItem = {
    ...newItem,
    id: 0, // Remove any existing id so the server can assign a new one
    meta: { isNew: true },
  };
  return { updatedItems: [...items, realNewItem], newItem: realNewItem };
};

const updateItem = (items, updatedItem) => {
  const updatedItems = [...items].map((currentItem) => {
    if (currentItem.id === updatedItem.id) {
      return { ...updatedItem };
    }
    return currentItem;
  });
  return { updatedItems, updatedItem };
};

const saveSettings = async (
  storeGuid,
  items,
  updatedId,
  shouldSendNotification,
  isItemActive,
  isDeliveryMarketplaceAuctaneFulfillment,
  action,
) => {
  const newItems = await writeToBackend(storeGuid, items, updatedId);
  if (shouldSendNotification) {
    const notificationTitle = isDeliveryMarketplaceAuctaneFulfillment
      ? messages.fulfillmentRateNotificationTitle
      : messages.checkoutRateNotificationTitle;
    let notificationMessage;
    if (isDeliveryMarketplaceAuctaneFulfillment) {
      notificationMessage = isItemActive
        ? messages.fulfillmentRateActiveNotificationContent
        : messages.fulfillmentRateInactiveNotificationContent;
    }
    if (action === ADD) {
      notificationMessage = messages.checkoutRateInactiveNotificationContent;
    } else {
      notificationMessage = isItemActive
        ? messages.editCheckoutRateActiveNotificationContent
        : messages.editCheckoutRateNotActiveNotificationContent;
    }
    NotificationService.showNotification({
      notificationType: NOTIFICATION_TYPE_SUCCESS,
      title: <FormattedMessage {...notificationTitle} />,
      content: <FormattedMessage {...notificationMessage} />,
      duration: NOTIFICATION_DURATIONS.MEDIUM,
    });
  }
  return newItems;
};

export const getDefaultItemShape = (weightUnit, dimensionUnit, homeCountry, sharedDefaultDimensionsByWeight, savedCalculationMethod) => {
  const defaultCalcMethod = homeCountry === AU || homeCountry === NZ ? BY_VOLUME : SKIP_DIMENSIONS;
  const defaultItem = {
    name: '',
    services: [],
    description: '',
    pricingModel: pricingModelEnum.liveRate,
    calculationMethod: savedCalculationMethod || defaultCalcMethod,
    hasDeactivatedService: false,
    flatRate: {
      value: 0,
      code: USD,
    },
    additionalFees: {
      mode: additionalFeesEnum.none,
      percentage: 0,
      flatRate: {
        value: 0,
        code: USD,
      },
    },
    fallbackOption: {
      mode: null,
      cartPercentage: 0,
      flatRate: {
        value: 0,
        code: USD,
      },
    },
    defaultDimensionsByWeightList: sharedDefaultDimensionsByWeight || [
      {
        defaultDimensions: {
          height: 0,
          width: 0,
          length: 0,
          unit: null,
        },
        maxWeight: {
          value: 0,
          unit: null,
        },
      },
      {
        defaultDimensions: {
          height: 0,
          width: 0,
          length: 0,
          unit: null,
        },
        maxWeight: {
          value: MAX_WEIGHT,
          unit: null,
        },
      },
    ],
    isActive: false,
  };
  defaultItem.defaultDimensionsByWeightList = defaultItem.defaultDimensionsByWeightList.map((element) => ({
    ...element,
    maxWeight: {
      ...element.maxWeight,
      unit: weightUnit,
    },
    defaultDimensions: {
      ...element.defaultDimensions,
      unit: dimensionUnit,
    },
  }));
  return defaultItem;
};

export const DeliveryOptionsContext = createContext();

export const DeliveryOptionsContextProvider = ({ children, storeGuid }) => {
  const marketplaceId = useSelector((state) => Global.selectSellerMarketplaceIdByStoreGuid(state, { storeGuid }));
  const marketplaceNameObject = GetMarketplaceDisplayName(marketplaceId);
  const marketplaceName = marketplaceNameObject.values?.name;
  const [items, setItems] = useAsyncSafeState([]);
  const [loading, setLoading] = useAsyncSafeState(true);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [itemToRemove, setItemToRemove] = useState();
  const [isRegistered, setIsRegistered] = useAsyncSafeState(false);
  const [error, setError] = useAsyncSafeState('');
  const [shopifyIsValid, setShopifyIsValid] = useAsyncSafeState(true);
  const [magentoIsValid, setMagentoIsValid] = useAsyncSafeState(true);
  const [woocommerceIsValid, setWooCommerceIsValid] = useAsyncSafeState(true);
  const [shouldUsePlanningToolRecommendationOnImport, setShouldUsePlanningToolRecommendationOnImport] = useAsyncSafeState(false);
  const [storeConfiguration, setStoreConfiguration] = useAsyncSafeState(emptyObject);
  const didMountRef = useRef(false);
  const sellerDefaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const sellerDefaultDimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const homeCountry = useSelector(Global.selectSellerHomeCountry);
  const [sharedDefaultDimensionsByWeight, setSharedDefaultDimensionsByWeight] = useState(
    getDefaultItemShape(sellerDefaultWeightUnit, sellerDefaultDimensionUnit, homeCountry).defaultDimensionsByWeightList,
  );
  const calcMethodType = useCalculationMethodType();
  const [calculationMethod, setCalculationMethod] = useState(calcMethodType === BY_WEIGHT_CALC_TYPE ? SKIP_DIMENSIONS : BY_VOLUME);
  const availableServices = useNonLocalDeliveryServicesAvailableForSeller();

  const isDeliveryMarketplaceAuctaneFulfillment = marketplaceId === MarketplaceIds.AuctaneFulfillment;
  const intl = useIntl();
  const [deliveryOptionsChangeId, setDeliveryOptionsChangeId] = useSubscribableState({ key: 'deliveryOptionsChangeId', initialState: 0 });

  const openPreviewModal = useCallback(() => {
    setShowPreviewModal(true);
  }, [setShowPreviewModal]);
  const closePreviewModal = useCallback(() => {
    setShowPreviewModal(false);
  }, [setShowPreviewModal]);

  const handleStatusCallback = useCallback(
    async (status) => {
      setIsRegistered(status.isRegistered);
      if (status.errorString) {
        setError(status.errorString);
        return;
      }
      if (!status.isRegistered) {
        return;
      }
      let newItems = await readFromBackend(storeGuid);
      newItems = newItems.map((item) => {
        if (item.pricingModel !== pricingModelEnum.liveRate) {
          return item;
        }
        const tempServices = item.services.filter((service) => {
          const targetSellerLabelProviderId = service.sellerProviderId.toString();
          const targetServiceId = service.serviceId.toString();
          return availableServices.find(
            (as) => as.sellerLabelProviderId === targetSellerLabelProviderId && as.serviceId === targetServiceId,
          );
        });
        if (tempServices.length === item.services.length) {
          return item;
        }
        return {
          ...item,
          services: tempServices,
          hasDeactivatedService: true,
        };
      });

      setItems(newItems);
      const liveRateItem = newItems.find((item) => item.defaultDimensionsByWeightList.length > 0);
      if (liveRateItem) {
        // if there are any pre-existing option settings they will all have the same values for this setting so we only need to take
        // the first one an the array
        const savedUnitOfMeasure = liveRateItem.defaultDimensionsByWeightList[0].defaultDimensions.unit;
        const savedUnitOfWeight = liveRateItem.defaultDimensionsByWeightList[0].maxWeight.unit;
        if (savedUnitOfMeasure !== sellerDefaultDimensionUnit) {
          liveRateItem.defaultDimensionsByWeightList = liveRateItem.defaultDimensionsByWeightList.map((element) => {
            const convertedDim = convertDimensions(element.defaultDimensions, sellerDefaultDimensionUnit);

            return {
              ...element,
              defaultDimensions: {
                ...element.defaultDimensions,
                unit: sellerDefaultDimensionUnit,
                width: convertedDim.width,
                length: convertedDim.length,
                height: convertedDim.height,
              },
            };
          });
        }

        if (savedUnitOfWeight !== sellerDefaultWeightUnit) {
          liveRateItem.defaultDimensionsByWeightList = liveRateItem.defaultDimensionsByWeightList.map((element) => {
            const convertedWeight = convertWeight(element.maxWeight, sellerDefaultWeightUnit);
            return {
              ...element,
              maxWeight: {
                ...element.maxWeight,
                value: convertedWeight.value,
                unit: sellerDefaultWeightUnit,
              },
            };
          });
        }

        setSharedDefaultDimensionsByWeight(liveRateItem.defaultDimensionsByWeightList);
        setCalculationMethod(liveRateItem.calculationMethod);
      }
    },
    [setIsRegistered, storeGuid, setItems, setError, availableServices, sellerDefaultDimensionUnit, sellerDefaultWeightUnit],
  );

  const registerStore = useCallback(() => {
    const asyncWrapper = async () => {
      setLoading(true);
      const status = await callRegisterStoreEndpoint(storeGuid);
      await handleStatusCallback(status);
      setLoading(false);
      try {
        await saveStoreSettings(storeGuid, {
          ...storeConfiguration,
          settings: {
            ...storeConfiguration.settings,
            usePlanningToolRecommendationOnImport: true,
          },
          shouldSkipServiceMappingUpdates: true,
        });
        setShouldUsePlanningToolRecommendationOnImport(true);
      } catch {
        NotificationService.showNotification({
          title: <FormattedMessage {...messages.notificationErrorTitle} />,
          notificationType: NOTIFICATION_TYPE_ERROR,
          content: <FormattedMessage {...messages.notificationErrorContent} />,
        });
      }
    };
    asyncWrapper();
  }, [setLoading, storeGuid, handleStatusCallback, storeConfiguration, setShouldUsePlanningToolRecommendationOnImport]);

  const removeItem = useCallback(
    async (item) => {
      const updatedItems = [...items]
        .filter((currentItem) => currentItem.id !== item.id)
        .map((currentItem, index) => ({ ...currentItem, index }));
      const serverItems = await saveSettings(storeGuid, updatedItems, undefined, false, undefined, isDeliveryMarketplaceAuctaneFulfillment);
      setItems(serverItems);
    },
    [isDeliveryMarketplaceAuctaneFulfillment, items, setItems, storeGuid],
  );
  const onDeleteCallback = useCallback((item) => setItemToRemove(item), [setItemToRemove]);
  const onDeleteCancel = useCallback(() => setItemToRemove(undefined), [setItemToRemove]);
  const onDeleteConfirm = useCallback(async () => {
    await removeItem(itemToRemove);
    setItemToRemove(undefined);
  }, [itemToRemove, removeItem, setItemToRemove]);

  const onUpdateCallback = useCallback(
    async (updatedItem) => {
      const { updatedItems } = updateItem(items, updatedItem);
      const serverItems = await saveSettings(
        storeGuid,
        updatedItems,
        updatedItem.id,
        true,
        updatedItem.isActive,
        isDeliveryMarketplaceAuctaneFulfillment,
      );
      setItems(serverItems);
      if (updatedItem.defaultDimensionsByWeightList?.length > 0) {
        setSharedDefaultDimensionsByWeight(updatedItem.defaultDimensionsByWeightList);
        setCalculationMethod(updatedItem.calculationMethod);
      }
      setDeliveryOptionsChangeId(deliveryOptionsChangeId + 1);
    },
    [deliveryOptionsChangeId, isDeliveryMarketplaceAuctaneFulfillment, items, setDeliveryOptionsChangeId, setItems, storeGuid],
  );

  const addNewItemCallback = useCallback(
    async (newItemParam) => {
      const { updatedItems, newItem } = addNewItem(items, newItemParam);
      const serverItems = await saveSettings(
        storeGuid,
        updatedItems,
        newItem.id,
        true,
        newItem.isActive,
        isDeliveryMarketplaceAuctaneFulfillment,
        ADD,
      );
      setItems(serverItems);
      if (newItem.defaultDimensionsByWeightList?.length > 0) {
        setSharedDefaultDimensionsByWeight(newItem.defaultDimensionsByWeightList);
        setCalculationMethod(newItem.calculationMethod);
      }
      setDeliveryOptionsChangeId(deliveryOptionsChangeId + 1);
    },
    [deliveryOptionsChangeId, isDeliveryMarketplaceAuctaneFulfillment, items, setDeliveryOptionsChangeId, setItems, storeGuid],
  );

  const copyItemCallback = useCallback(
    async (newItem) => {
      const newName = `${intl.formatMessage(messages.copyAppend, { name: newItem.name })}`;
      await addNewItemCallback({ ...newItem, name: newName });
    },
    [addNewItemCallback, intl],
  );

  useEffect(() => {
    if (didMountRef.current) {
      return;
    }
    const asyncWrapper = async () => {
      setLoading(true);
      const status = await getStoreStatus(storeGuid);
      if (!status.isRegistered && marketplaceId === MarketplaceIds.Shopify) {
        const validShopify = await getShopifyIsValid(storeGuid);
        setShopifyIsValid(validShopify);
      }
      if (!status.isRegistered && marketplaceId === MarketplaceIds.Magento) {
        const validMagento = await getMagentoIsValid(storeGuid);
        setMagentoIsValid(validMagento);
      }
      if (!status.isRegistered && marketplaceId === MarketplaceIds.WooCommerce) {
        const validWooCommerce = await getWooCommerceIsValid(storeGuid);
        setWooCommerceIsValid(validWooCommerce);
      }
      await handleStatusCallback(status);
      const currentStore = await getStore(storeGuid);
      setStoreConfiguration(currentStore);
      if (currentStore.settings.usePlanningToolRecommendationOnImport) {
        setShouldUsePlanningToolRecommendationOnImport(true);
      }
      setLoading(false);
      didMountRef.current = true;
    };
    asyncWrapper();
  }, [
    setItems,
    setLoading,
    setIsRegistered,
    setError,
    setShopifyIsValid,
    storeGuid,
    marketplaceId,
    handleStatusCallback,
    setMagentoIsValid,
    setWooCommerceIsValid,
    setShouldUsePlanningToolRecommendationOnImport,
    setStoreConfiguration,
  ]);

  const contextMessages = useMemo(() => {
    return isDeliveryMarketplaceAuctaneFulfillment
      ? {
          activeAndVisibleCheckbox: messages.activeAndVisibleFulfillmentCheckbox,
          addRate: messages.addFulfillmentRate,
          adjustPercentageDescription: messages.fulfillmentAdjustPercentageDescription,
          adjustSetAmountDescription: messages.fulfillmentAdjustSetAmountDescription,
          defineRateTitle: messages.defineFulfillmentRateTitle,
          flatRateDescription: messages.flatRatePriceDescription,
          flatRatePrice: messages.flatRatePrice,
          ratesHeader: messages.fulfillmentRatesHeader,
          missingRatesMessage: messages.haventAddedFulfillmentRates,
          title: messages.fulfillmentRates,
        }
      : {
          activeAndVisibleCheckbox: messages.activeAndVisibleCheckbox,
          addRate: messages.addCheckoutRate,
          adjustPercentageDescription: messages.adjustPercentageDescription,
          adjustSetAmountDescription: messages.adjustSetAmountDescription,
          defineRateTitle: messages.defineCheckoutRateTitle,
          deliveryOptionDescription: messages.deliveryOptionDescription,
          flatRateDescription: messages.flatRateDescription,
          flatRatePrice: messages.flatRateConfiguration,
          ratesHeader: messages.checkoutRatesHeader,
          missingRatesMessage: messages.haventAddedCheckoutRates,
          title: messages.checkoutRates,
        };
  }, [isDeliveryMarketplaceAuctaneFulfillment]);

  const value = useMemo(() => {
    return {
      items,
      loading,
      marketplaceName,
      marketplaceNameObject,
      copyItemCallback,
      isRegistered,
      error,
      registerStore,
      storeGuid,
      shopifyIsValid,
      magentoIsValid,
      woocommerceIsValid,
      showPreviewModal,
      openPreviewModal,
      closePreviewModal,
      itemToRemove,
      onDeleteCallback,
      onDeleteCancel,
      onDeleteConfirm,
      addNewItemCallback,
      onUpdateCallback,
      shouldUsePlanningToolRecommendationOnImport,
      setShouldUsePlanningToolRecommendationOnImport,
      marketplaceId,
      sharedDefaultDimensionsByWeight,
      contextMessages,
      isDeliveryMarketplaceAuctaneFulfillment,
      calculationMethod,
      setCalculationMethod,
      setItems,
    };
  }, [
    items,
    loading,
    marketplaceName,
    marketplaceNameObject,
    copyItemCallback,
    isRegistered,
    error,
    registerStore,
    storeGuid,
    shopifyIsValid,
    magentoIsValid,
    woocommerceIsValid,
    showPreviewModal,
    openPreviewModal,
    closePreviewModal,
    itemToRemove,
    onDeleteCallback,
    onDeleteCancel,
    onDeleteConfirm,
    addNewItemCallback,
    onUpdateCallback,
    shouldUsePlanningToolRecommendationOnImport,
    setShouldUsePlanningToolRecommendationOnImport,
    marketplaceId,
    sharedDefaultDimensionsByWeight,
    contextMessages,
    isDeliveryMarketplaceAuctaneFulfillment,
    calculationMethod,
    setCalculationMethod,
    setItems,
  ]);

  return <DeliveryOptionsContext.Provider value={value}>{children}</DeliveryOptionsContext.Provider>;
};

DeliveryOptionsContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  storeGuid: PropTypes.string.isRequired,
};
